import type { Resources } from "./ru";

export const en: Resources = {
  translation: {
    common: {
      appName: "GUDWIN",
      loading: "Loading…",
      error: "Error",
      apiError: {
        fallback: "An error occurred. Contact your system administrator.",
        requestId: "Request identifier: {{id}}",
      },
      retry: "Retry",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      confirm: "Confirm",
      close: "Close",
      clear: "Clear",
      back: "Back",
      notFound: "Page not found",
      elementLabel: "Element: {{name}}",
      statusToggle: {
        on: "ACTIVE",
        off: "OFF",
      },
      combobox: {
        searchPlaceholder: "Search…",
        empty: "Nothing found",
      },
      multiSelect: {
        selectAll: "Select all",
        clearAll: "Clear all",
        selected: "Selected: {{selected}} of {{total}}",
      },
      dateField: {
        placeholder: "Choose a date",
        previousMonth: "Previous month",
        nextMonth: "Next month",
        clear: "Clear date",
        weekdays: {
          mon: "Mon",
          tue: "Tue",
          wed: "Wed",
          thu: "Thu",
          fri: "Fri",
          sat: "Sat",
          sun: "Sun",
        },
      },
      workTypeSet: {
        addCta: "Add work type",
        searchPlaceholder: "Search work type…",
        searchEmpty: "Nothing found",
        empty: "No work types yet.",
        remove: "Remove “{{name}}”",
        removeConfirmTitle: "Remove work type?",
        removeConfirmBody: "Work type “{{name}}” will be removed from the set permanently.",
      },
      passwordInput: {
        show: "Show password",
        hide: "Hide password",
      },
      relativeTime: {
        today: "today",
      },
      releaseUpdate: {
        available: "An update is available",
        action: "Update",
      },
    },
    validation: {
      required: "This field is required",
      invalidType: "Invalid value",
      invalidValue: "Invalid value",
      invalidFormat: "Invalid format",
      notMultipleOf: "Invalid value",
      tooShort: "At least {{min}} characters",
      tooLong: "No more than {{max}} characters",
      numberTooSmall: "At least {{min}}",
      numberTooBig: "At most {{max}}",
      tooSmall: "Too small (minimum {{min}})",
      tooBig: "Too big (maximum {{max}})",
      endBeforeStart: "End date cannot be earlier than the start date",
      emailInvalid: "Enter a valid email",
      emailReservedDomain: "This email domain is reserved. Enter a different address",
      fieldErrorCount_one: "Fix {{count}} field",
      fieldErrorCount_other: "Fix {{count}} fields",
      fieldErrorCount_few: "Fix {{count}} fields",
      fieldErrorCount_many: "Fix {{count}} fields",
    },
    errorBoundary: {
      eyebrow: "Failure",
      title: "Something went wrong",
      body: "The page failed to render. The error is already in the server log — share the identifier below with your administrator.",
      requestIdLabel: "Request identifier",
      requestIdHint: "Copy the identifier and send it to your administrator for investigation.",
      noRequestId: "Request identifier is not available — this is a local UI failure.",
      copy: "Copy",
      copied: "Identifier copied",
      copyFailed: "Could not copy",
      reload: "Reload",
    },
    nav: {
      breadcrumb: "Breadcrumbs",
      home: "Home",
      projects: "Projects",
      approvals: "Simulations",
      risks: "Risks",
      portfolio: "Portfolio",
      load: "Resource load",
      units: "Departments and units",
      people: "People",
      new: "New person",
      templates: "Templates",
      audit: "Audit",
      calendar: "Production calendar",
      settings: "Settings",
      "change-password": "Change password",
      admin: "Admin",
      users: "Employees",
      catalogs: "Catalogs",
      "time-approval": "Time tracking",
      "work-status": "Work statuses",
      login: "Sign in",
      collapse: "Collapse",
      expand: "Expand",
      approvalDetail: "Simulation",
      section: {
        myWork: "My work",
        decisions: "Decisions",
        reference: "Settings",
      },
    },
    peopleHub: {
      title: "Employees",
      tabs: {
        employees: "Employees",
        structure: "Organization",
        availability: "Absences and overtime",
      },
    },
    peopleCard: {
      readOnly: "This profile is read-only",
    },
    workStatus: {
      title: "Work statuses",
      pickUnit: "Pick a unit to see its work",
      empty: "No work in this unit yet — entries appear once people report time",
      emptySearch: "Nothing found",
      search: {
        aria: "Search work",
        placeholder: "Person, work or project",
      },
      filters: {
        reset: "Reset",
      },
      updated: {
        actualized: "Updated by the lead: {{at}}",
        tracked: "Not updated by the lead yet. Last tracked day: {{at}}",
      },
      columns: {
        work: "Work",
        performer: "Performed by",
        status: "Status",
        forecast: "Forecast",
        dueDate: "Due",
        checkpoints: "Checkpoints",
        updated: "Updated",
      },
      status: {
        planned: "Planned",
        in_progress: "In progress",
        paused: "Paused",
        completed: "Completed",
      },
      forecast: {
        gt2w: "> 2 wk",
        w2: "2 wk",
        w1: "1 wk",
        d3: "3 days",
        d1: "1 day",
      },
      checkpoints: {
        done: "{{title}} — done",
        open: "{{title}} — not done",
      },
      overdueBadge_one: "Overdue by {{count}} day",
      overdueBadge_few: "Overdue by {{count}} days",
      overdueBadge_many: "Overdue by {{count}} days",
      overdueBadge_other: "Overdue by {{count}} days",
      panel: {
        status: "Status",
        forecast: "Forecast",
        completedAt: "Completion date",
        dueDate: "Due",
        dueDateEmpty: "No due date — click to set",
        completedAtHint: "Hours tracked after this date count as rework.",
        checkpoints: "Checkpoints",
        noCheckpoints: "This work has no checkpoints",
        addCheckpoint: "Add checkpoint",
        addCheckpointPlaceholder: "New checkpoint",
        removeCheckpoint: "Remove checkpoint",
        removeCheckpointConfirmTitle: "Remove this checkpoint?",
        removeCheckpointConfirmBody:
          "“{{title}}” will be removed from this work along with its completion mark. The removal stays in the history.",
        comment: "Comment",
        commentPlaceholder: "A note on the change — it goes to the log with the date and author",
        history: "History",
        noHistory: "No changes yet",
        noPerformer: "No performer yet",
        updatedAt: "updated {{when}}",
        event: {
          status: "Status: {{from}} → {{to}}",
          forecast: "Forecast: {{from}} → {{to}}",
          completedAt: "Completion date: {{from}} → {{to}}",
          dueDate: "Due: {{from}} → {{to}}",
          memoQuote: "“{{memo}}”",
          checkpoint: {
            done: "Checkpoint “{{title}}” done",
            pending: "Checkpoint “{{title}}” unmarked",
            added: "Checkpoint “{{title}}” added",
            removed: "Checkpoint “{{title}}” removed",
          },
        },
      },
    },
    timeApproval: {
      title: "Time tracking",
      subtitle:
        "Submitted unit reports to review and approve; enter and edit time on behalf of staff",
      selfSubtitle: "Enter and sign off your working time",
      tabs: {
        console: "Approval",
        entry: "Fill time",
        history: "History",
      },
      entryTab: {
        dateLabel: "Date",
        selfHint: "Me · supervisor",
        selfSubjectLabel: "Your working time",
        empty: "Pick a staff member and a date to enter or edit time",
      },
      week: "Week",
      prevWeek: "Previous week",
      nextWeek: "Next week",
      thisWeek: "Current week",
      legendTitle: "Legend",
      legendPending: "Awaiting approval — your action",
      legendMissing: "Not submitted — no report yet",
      legendDone: "Cleared — day is closed",
      ctxPending: "Awaiting approval: {{count}}",
      ctxMissing: "Not submitted: {{count}}",
      ctxApproved: "Approved: {{count}}",
      chipDoneAria: "All cleared",
      chipNoDataAria: "No data",
      noUnit: "No unit is assigned to you",
      loadError: "Failed to load the week summary",
      feedHeader: "Submitted · {{submitted}}",
      feedEmpty: "No submitted reports for this day",
      you: "YOU",
      badgeWaiting: "awaiting",
      discTitle: "Discrepancy",
      discOverNorm: "Timesheet above the shift norm",
      discUnderNorm: "Timesheet below the shift norm",
      discAbsence: "Worked during an absence: {{name}}",
      markBadge: "absent",
      markReason: "Reason",
      badgeApproved: "approved",
      footNorm: "Norm",
      footTasks: "Total work",
      footDeclaredVsNorm: "Timesheet vs norm",
      elementMissing: {
        title: "No element selected",
        body: "The employee did not specify an element. Follow up and complete the report if needed.",
      },
      edit: "Edit",
      openDay: "Open day · {{name}}",
      approve: "Approve",
      cancelApproval: "Undo",
      approving: "Approving…",
      compactDeclared: "TS",
      compactTasks: "Tasks",
      approveError: "Failed to approve the report",
      cancelError: "Failed to undo the approval",
      missingTitle: "Not submitted · {{count}}",
      missingExpand: "Review missing",
      missingCollapse: "Collapse",
      missingHint: "expected by schedule · not on leave / day off",
      missingEmpty: "Everyone on schedule has reported",
      missingBadge: "no report",
      enter: "Enter",
      absence: "Absence",
      dockEnterTitle: "Enter for employee",
      dockEditTitle: "Edit report",
      dockSelfEnterTitle: "Enter your time",
      dockSelfEditTitle: "Edit your time",
      dockEnteredByLead: "no report · entered by lead",
      dockEditClarify: "Clarifying the employee's report",
      dockSubject: "Employee · day",
      dockDeclared: "Timesheet, h",
      dockRecords: "Works and tasks",
      dockNoRecords: "— no records —",
      dockAddRecord: "Add work",
      dockEditRecord: "Edit record",
      dockConfirm: "Confirm",
      dockSign: "Sign",
      dockSaving: "Saving…",
      composerProject: "Project",
      composerWorkType: "Work",
      composerHours: "Hours",
      composerFreeWorkType: "Free text",
      composerAdd: "Add",
      composerCancel: "Cancel",
      absenceTitle: "Mark absence",
      absenceReasonLabel: "Reason",
      absenceReasonNoShow: "No-show",
      absenceReasonSick: "Sick leave",
      absenceReasonDayOff: "Day off",
      absenceReasonOther: "Unpaid leave",
      absenceNote: "Comment",
      absenceSave: "Mark absence",
      absenceSaved: "Absence marked",
      absenceError: "Failed to mark absence",
      enteredOk: "Report entered and approved",
      signedOk: "Report signed",
      enterError: "Failed to enter the report",
      entryDeleted: "Record deleted",
      entryDeleteError: "Failed to delete the record",
      entryDeleteTitle: "Delete time entry?",
      entryDeleteBody: "Entry “{{work}} · {{hours}}” belonging to {{person}} will be deleted.",
      unitPicker: {
        label: "Unit",
        placeholder: "Select a unit",
        leadHint: "Lead: {{name}}",
        empty: "Select a unit to open its reports",
      },
      workerPicker: {
        label: "Employee",
        placeholder: "Select an employee to open their day",
      },
    },
    week: {
      progress: "Confirmed {{confirmed}} / {{total}}",
      notRequiredCount: "Does not require confirmation: {{count}}",
      nothingToApprove: "Nothing to confirm",
      approveAll: "Confirm all",
      loadError: "Could not load the weekly feed",
      empty: "No weekly reports for this week",
      lens: {
        label: "Approval mode",
        day: "Day",
        week: "Week",
      },
      cell: {
        noReport: "no report",
        workLabel: "Work",
        declaredLabel: "Timesheet",
        undertimeTooltip: "Undertime: {{hours}}",
        overtimeTooltip: "Overtime: {{hours}}",
      },
      card: {
        daysLabel: "Report coverage",
        worksLabel: "Project work",
        weekWork: "Work this week: {{hours}}",
        weekDeclared: "Timesheet this week: {{hours}}",
        tabNo: "ID {{number}}",
        confirmed: "Confirmed",
        notRequired: "Does not require confirmation",
        expand: "Expand",
        collapse: "Collapse",
        unapprove: "Undo confirmation",
        blockedHint: "Fill the empty days to confirm the week",
        blockedHintDraft: "Complete the drafts to confirm the week",
        blockedHintMixed: "Fill the empty days and complete the drafts to confirm the week",
        markAbsence: "Register absence",
        problems_one: "{{count}} issue",
        problems_few: "{{count}} issues",
        problems_many: "{{count}} issues",
        problems_other: "{{count}} issues",
      },
      summary: {
        title: "Week approval · {{range}}",
        confirmed_one: "Confirmed: {{count}} person · {{hours}}.",
        confirmed_few: "Confirmed: {{count}} people · {{hours}}.",
        confirmed_many: "Confirmed: {{count}} people · {{hours}}.",
        confirmed_other: "Confirmed: {{count}} people · {{hours}}.",
        rejectedTitle: "Not confirmed · needs fixing",
        notRequiredTitle: "Did not require confirmation: {{count}}.",
        noReport: "No report for {{date}}",
        draftReport: "Draft report for {{date}}",
        ok: "Got it",
      },
    },
    leadHub: {
      title: "Lead workspace",
      approval: "Time approval",
      approvalHint: "Review and approve unit reports",
      workStatus: "Work status",
      workStatusHint: "Track how the unit's work is progressing",
      myTime: "My time",
      myTimeHint: "Enter your own work time",
      logout: "Log out",
      back: "Back",
      approvalScreenTitle: "Approval",
    },
    templates: {
      title: "Project templates",
      subtitle: "Active and draft template versions",
      newTemplate: "New template",
      search: "Search by name",
      statusFilter: {
        all: "All",
        hasActive: "With active",
        hasDraft: "With drafts",
        archivedOnly: "Archived only",
      },
      columns: {
        name: "Template",
        latestActive: "Active",
        draft: "Draft",
        versions: "Versions",
        actions: "Actions",
      },
      status: {
        active: "active",
        draft: "draft",
        archived: "archived",
        none: "—",
      },
      actions: {
        editForks: "Edit",
        editDraft: "Edit",
        promote: "Promote",
        archive: "Archive",
        clone: "Clone",
        deleteDraft: "Delete draft",
      },
      empty: "No templates yet",
      readOnly: "Only a project manager or admin can edit templates.",
      newDialog: {
        title: "New template",
        name: "Name",
        nameHint: "Shown in the list and in project headers.",
        submit: "Create draft",
      },
      cloneDialog: {
        title: "Clone template",
        templateId: "New identifier",
        name: "New name",
        submit: "Clone",
      },
      promoteDialog: {
        title: "Promote draft?",
        body: "The draft will become the active version.",
        bodyWithActive: "Active version v{{version}} will be archived.",
        submit: "Promote",
      },
      archiveDialog: {
        title: "Move to archive?",
        body: "Template “{{name}}” will be hidden from the new-project wizard.",
        submit: "Archive",
      },
      deleteDraftDialog: {
        title: "Delete draft?",
        body: "Draft “{{name}}” will be deleted permanently. The active version is not affected.",
        submit: "Delete",
      },
      toasts: {
        created: "Draft created",
        promoted: "Template promoted",
        archived: "Template archived",
        cloned: "Template cloned",
        deleted: "Draft deleted",
        forkOpened: "Draft opened for editing",
      },
    },
    editor: {
      empty: "No tasks yet — use the button above to add one.",
      addTask: "Add task",
      save: "Save",
      saveAndPromote: "Save & Promote",
      discard: "Delete draft",
      tabs: {
        workflow: "Diagram",
        gantt: "Preview",
      },
      workflow: {
        directionLR: "Left to right",
        directionTB: "Top to bottom",
      },
      banner: {
        editingDraftWithActive: "Editing v{{version}} · active is v{{activeVersion}}",
        editingDraftNoActive: "Editing v{{version}} · no active version",
        readOnly: "Read-only · {{status}} v{{version}}",
      },
      validation: {
        title: "Validation",
        empty: "No issues found.",
        pill: "Validation: {{errors}} errors, {{warnings}} warnings",
        levelError: "error",
        levelWarning: "warning",
        errors: {
          task_no_anchor_or_dep:
            "Task «{{task}}»: must have either an anchor or at least one dependency.",
          task_anchor_and_dep:
            "Task «{{task}}»: cannot have both an anchor and predecessors — pick one.",
          task_milestone_effort:
            "Task «{{task}}» (milestone, kind {{kind}}): effort, people and duration must all be zero.",
          task_regular_effort:
            "Task «{{task}}»: effort and default people must be greater than zero.",
          duplicate_task_ids: "Duplicate task identifiers in template: {{ids}}.",
          duplicate_param_names: "Duplicate parameter names in template: {{names}}.",
          task_unknown_dep: "Task «{{task}}»: dependency references unknown task «{{dep}}».",
          task_anchor_unknown_param:
            "Task «{{task}}»: anchor references unknown parameter «{{param}}».",
          task_anchor_param_not_date:
            "Task «{{task}}»: anchor parameter «{{param}}» must be type 'date', got '{{got}}'.",
          task_dep_unknown_param:
            "Task «{{task}}»: dependency lag references unknown parameter «{{param}}».",
          dependency_cycle: "Dependency cycle detected: {{chain}}.",
        },
      },
      params: {
        toggle: "Parameters ({{count}})",
        empty: "No parameters yet.",
        add: "Add parameter",
        addCta: "Add",
        required: "Required",
        types: {
          date: "date",
          int: "int",
          string: "string",
        },
        required_short: "req.",
        errors: {
          nameFormat: "Letters, digits, _ and - only. Must start with a letter or _.",
          duplicate: "Parameter with this name already exists.",
        },
      },
      gantt: {
        col: {
          unit: "Unit",
          name: "Task",
        },
      },
      props: {
        id: "ID",
        name: "Name",
        unit: "Unit",
        unitPlaceholder: "Select a unit",
        kind: "Kind",
        kindOptions: {
          task: "Task",
          milestone: "Milestone",
          external_milestone: "External dependency",
        },
        defaultPeople: "Default people",
        effort: "Effort, h",
        durationComputed: "Computed duration: {{n}} working days",
        anchorOrDeps: "Anchor / Dependencies",
        noSelection: "Pick a task in the list or Gantt.",
        notes: "Notes",
      },
      anchor: {
        byParam: "By parameter",
        byPredecessor: "By predecessor",
        byPredecessorHint:
          "Links are managed below in the “Links” section. To create a link, drag on the diagram from the end of a task to another; to edit the lag, use the “Links” section.",
      },
      task: {
        defaultName: "New task",
      },
      link: {
        from: "From",
        to: "To",
        type: "Type",
        lagNumber: "Days",
        lagParam: "Parameter",
        delete: "Delete link",
        close: "Close",
        sectionTitle: "Predecessor links",
        addLink: "+ Add link",
        noLinks: "No links.",
        chooseSource: "— pick a task —",
        lagLabel: "Start delay",
        lagDays: "d.",
        edgeInspectorTitle: "Dependency properties",
        depType: {
          FS: { full: "Finish to Start", short: "FS" },
          FF: { full: "Finish to Finish", short: "FF" },
          SS: { full: "Start to Start", short: "SS" },
          SF: { full: "Start to Finish", short: "SF" },
        },
        depTypeHint: {
          FS: "Successor cannot start until the predecessor finishes.",
          FF: "Successor cannot finish until the predecessor finishes.",
          SS: "Successor can only start after the predecessor starts.",
          SF: "Successor can only finish after the predecessor starts.",
        },
      },
      deleteConfirm: {
        title: "Confirm deletion",
        taskBody: "Task “{{name}}” will be removed from the draft permanently.",
        linkBody: "The link from “{{from}}” to “{{to}}” will be removed from the draft.",
        parameterBody: "Parameter “{{name}}” will be removed from the draft permanently.",
        tasksBody: "Tasks “{{names}}” will be removed from the draft permanently.",
      },
      discardDialog: {
        title: "Delete draft?",
        body: "Draft “{{name}}” will be deleted permanently. The active version is not affected.",
        submit: "Delete",
      },
      toasts: {
        saved: "Saved",
        promoted: "Template promoted",
        discarded: "Draft deleted",
      },
    },
    layout: {
      agentStub: {
        aria: "Gudwin Agent (soon)",
        title: "Gudwin Agent",
        body: "AI agent under development. Once live: morning risk digests, replanning and project creation via Telegram and this panel. People stay in the loop — every change goes through an approval queue.",
      },
      skeletonNote: "PR9b-1 skeleton — real layout, auth and widgets land in PR 10.",
      bellAriaLabel: "Notifications",
      bellTitle_one: "{{count}} pending approval",
      bellTitle_other: "{{count}} pending approvals",
      bellTitle_few: "{{count}} pending approvals",
      bellTitle_many: "{{count}} pending approvals",
      bellEmpty: "No pending simulations",
      bellSeeAll: "All approvals →",
      userMenu: {
        settings: "Settings",
        logout: "Sign out",
      },
    },
    role: {
      admin: "Administrator",
      viewer: "Viewer",
      office_staff: "Office staff",
      worker: "Production worker",
    },
    home: {
      title: "GUDWIN",
      subtitle: "Snapshot of your projects and approvals",
      pocLink: "/poc/gantt — PR 9 gating PoC",
      widgets: {
        empty: "No widgets configured for your role.",
        portfolioOverview: {
          title: "Project portfolio",
          activeBig: "Projects in progress",
          rows: {
            in_progress: "In progress",
            planned: "Planned",
            completed: "Completed",
          },
        },
        loadSummary: {
          title: "Unit load",
          subtitle: "Top-5 for 14 days",
          rowFmt: "{{peak}}/{{capacity}} h · {{overload}} overload day(s)",
          empty: "All units within capacity for the next 14 days.",
        },
        recentChanges: {
          title: "Recent changes",
          empty: "No changes yet.",
        },
        pendingApprovals: {
          title: "Pending approvals",
          empty: "No active simulations",
          expires: "Expires: {{when}}",
        },
        topRisks: {
          title: "Active risks",
          empty: "No risks",
          severity: {
            critical: "Critical",
            high: "High",
            medium: "Medium",
            low: "Low",
          },
          type: {
            overdue_task: "Overdue task",
            approaching_deadline: "Approaching deadline",
            resource_overload: "Resource overload",
            dangling_dependency: "Dangling dependency",
            critical_path_slippage: "Critical-path slippage",
          },
        },
      },
    },
    locale: {
      label: "Language",
      ru: "Русский",
      en: "English",
    },
    login: {
      title: "Sign in",
      email: "Email",
      password: "Password",
      submit: "Sign in",
      submitting: "Signing in…",
      invalidEmail: "Enter a valid email",
      passwordRequired: "Password is required",
      invalidCredentials: "Invalid email or password",
      networkError: "Network error, please try again",
      workerMode: {
        switchTo: "Sign in with employee number",
        switchBack: "Email sign-in",
        divider: "or",
      },
    },
    settings: {
      title: "Settings",
      changePasswordLink: "Change password",
      language: "Interface language",
    },
    projects: {
      title: "Projects",
      subtitle: "Overview of all active and planned projects",
      breadcrumb: {
        portfolio: "PORTFOLIO",
        projects: "PROJECTS",
      },
      counterUnit: "PROJECTS",
      newCta: "New project",
      empty: "No projects",
      searchAria: "Search by name, customer, or code",
      searchPlaceholder: "Search…",
      status: "Status",
      atRisk: "At risk",
      showArchived: "Show archived",
      archivedBadge: "Archived",
      overdueBadge_one: "{{count}} day overdue",
      overdueBadge_few: "{{count}} days overdue",
      overdueBadge_many: "{{count}} days overdue",
      overdueBadge_other: "{{count}} days overdue",
      fromTemplate: "From template {{template}} v{{version}}",
      fromTemplateShort: "{{template}} v{{version}}",
      prev: "Previous",
      next: "Next",
      pageIndicator: "Page {{page}} of {{total}}",
      pageRange: "{{from}}–{{to}} of {{total}}",
      dateRange: "{{start}} → {{end}}",
      noManager: "—",
      statusValues: {
        all: "All",
        planned: "Planned",
        in_progress: "In progress",
        on_hold: "On hold",
        completed: "Completed",
        cancelled: "Cancelled",
        at_risk: "At risk",
      },
      columns: {
        code: "Number",
        name: "Name",
        customer: "Customer",
        status: "Status",
        manager: "Project manager",
        dates: "Dates",
        progress: "Progress",
        plannedStart: "Start",
        plannedEnd: "End",
      },
      tabs: {
        params: "Parameters",
        tasks: "Tasks",
        gantt: "Gantt",
        risks: "Risks",
        history: "History",
      },
      detail: {
        notes: "Notes",
        stats: {
          progress: "Progress",
          tasks: "Tasks",
          daysToEnd: "Time left",
        },
        workdays_one: "{{count}} working day",
        workdays_few: "{{count}} working days",
        workdays_many: "{{count}} working days",
        workdays_other: "{{count}} working days",
      },
      params: {
        edit: "Edit",
        saved: "Saved",
        archive: "Archive",
        archiveConfirmTitle: "Archive project?",
        archiveConfirmBody: "Project “{{name}}” will be hidden from active work lists.",
        unarchive: "Unarchive",
        archived: "Project archived",
        unarchived: "Project restored from archive",
        delete: "Delete permanently",
        deleted: "Project deleted",
        dangerTitle: "Danger zone",
        deleteDescription: "Only archived projects without timesheet history can be deleted.",
        deleteConfirmTitle: "Delete project permanently?",
        deleteConfirmPrompt: "Enter project number {{code}} to confirm.",
        deleteConfirmAria: "Project number confirmation",
        deleteHasTimesheetHistory:
          "The project cannot be deleted because it has timesheet history.",
        deleteBlocked: "The project cannot be deleted.",
        close: "Close project",
        closeConfirmTitle: "Close project?",
        closeConfirmBody:
          "The project will be marked complete, with today as the actual finish date.",
        closeConfirmAction: "Close project",
        closed: "Project closed",
        discardTitle: "Discard changes?",
        discardBody: "You have unsaved changes — they will be lost.",
        discardAction: "Discard changes",
        metricsEmpty: "Parameters appear after you select an equipment type",
        dirtyClean: "no changes",
        dirtyPending: "unsaved changes",
        sections: {
          identification: "Identification",
          dates: "Timeline",
          responsible: "Owner",
          equipment: "Equipment",
          notes: "Notes",
        },
        fields: {
          code: "Project number",
          status: "Status",
          name: "Name",
          customer: "Customer",
          plannedStart: "Planned start",
          plannedEnd: "Planned finish",
          actualEnd: "Actual finish",
          manager: "Project manager",
          managerPlaceholder: "Select a project manager",
          notes: "Notes",
        },
      },
      equipment: {
        sectionLegend: "Report composition",
        typeLabel: "Equipment type",
        typePlaceholder: "Select an equipment type",
        typeClear: "Clear equipment type",
        quantityLabel: "Quantity",
        loading: "Loading composition…",
        error: "Couldn't load the composition",
        emptyElements: "This type has no elements.",
        requiredTitle: "Required elements",
        requiredLockHint: "Required element — always part of the product",
        selectedOptionsTitle: "Included options",
        optionalTitle: "Options",
        yes: "Yes",
        no: "No",
        optionAria: "{{name}}: part of the product?",
        optionUndecidedHint: "Decide on each option — Yes or No.",
        optionUndecidedError: "Answer Yes or No for each option.",
        noElementHint: "Element missing? Ask the process engineer — they maintain the catalog.",
        saveElementsFailed: "Project created, but the element composition couldn't be saved.",
        metrics: {
          title: "Parameter values",
          orphanGroup: "Without element",
          loading: "Loading parameters…",
          error: "Couldn't load the parameters",
          choicePlaceholder: "Select a value",
        },
      },
      risks: {
        empty: "No risks for this project",
        includeResolved: "Show resolved",
        hideResolved: "Hide resolved",
        resolvedAt: "resolved {{when}}",
      },
      history: {
        empty: "No audit entries",
      },
      gantt: {
        empty: "No tasks with dates to display",
        zoom: "Zoom",
        today: "Today",
        zooms: {
          day: "Day",
          week: "Week",
          month: "Month",
          quarter: "Quarter",
        },
      },
      new: {
        title: "New project",
        subtitle: "Pick a template and fill in parameters.",
        backToList: "← Back to list",
        back: "Back",
        create: "Create project",
        creating: "Creating…",
        applied: "Project created",
        noTemplates: "No active templates yet. Upload one via /templates.",
        parametersLegend: "Template parameters",
        parametersCount_one: "{{count}} parameter",
        parametersCount_other: "{{count}} parameters",
        parametersCount_few: "{{count}} parameters",
        parametersCount_many: "{{count}} parameters",
        steps: {
          template: "Template",
          params: "Parameters",
        },
        fields: {
          code: "Project number",
          projectName: "Project name",
          customerName: "Customer",
          start: "Start",
          end: "End",
          calendar: "Calendar",
          manager: "Project manager",
          managerPlaceholder: "Select a project manager",
        },
        errors: {
          required: "This field is required",
          dateFormat: "Use YYYY-MM-DD",
          integer: "Must be an integer",
          codeRequired: "Enter a project code",
          codeConflict: "This project number is already in use",
          projectNameRequired: "Project name is required",
          startRequired: "Pick a start date",
          endRequired: "Pick an end date",
          endBeforeStart: "End date cannot be earlier than start",
          positiveInteger: "Enter a whole number of at least 1",
          calendarRequired: "Pick a calendar",
          managerRequired: "Pick a manager",
          createFailed: "Failed to create the project",
          forbidden: "Only a project manager or admin can create projects",
        },
      },
      tasks: {
        empty: "No tasks in this project",
        criticalPath: "On critical path",
        criticalPathShort: "CP",
        pin: "Pin",
        unpin: "Unpin",
        edit: "Edit",
        columns: {
          name: "Task",
          unit: "Unit",
          plannedStart: "Start",
          plannedEnd: "End",
          progress: "Progress",
          actions: "Actions",
        },
        statuses: {
          planned: "Planned",
          in_progress: "In progress",
          blocked: "Blocked",
          completed: "Completed",
          cancelled: "Cancelled",
        },
        statusBadges: {
          inProgress: "in progress",
        },
        filters: {
          unit: "Unit",
          allUnits: "All",
          criticalOnly: "Critical only",
        },
        createCta: "Task",
        overtime: {
          title: "Overtime",
          openAria: "Add overtime",
          range: "Range",
          from: "From",
          to: "To",
          hoursPerPerson: "Hours per person",
          hoursHint: "Default shift is {{default}} h.",
          invalidRange: "End date cannot be before start date",
          previewTitle: "Estimate (excluding weekends)",
          previewDays: "Working days in window: {{count}}",
          previewHours: "Extra person-hours: {{hours}} ({{people}} people)",
          previewNote:
            "Exact values come from the backend simulation against the company calendar.",
          failed: "Failed to create overtime simulation",
          picks: {
            today: "Today",
            this_week: "This week",
            next_week: "Next week",
            rest_of_month: "Rest of month",
          },
        },
        modal: {
          title: "Edit task",
          save: "Save",
          saving: "Saving…",
          simulate: "Simulate",
          simulating: "Simulating…",
          applyDirect: "Apply without simulation",
          applyDirectTooltip:
            "Direct save without simulating cascade effects. Simulation for this field is on the roadmap — see backlog 54.",
          applying: "Applying…",
          simulateNeedsDate: "Simulation requires a different planned start.",
          simulateFailed: "Failed to create simulation",
          applyOk: "Changes applied",
          applyFailed: "Failed to apply changes",
          conflict: "Stale data — refresh the page and try again.",
          noChanges: "Nothing to save",
          reasonRequired: "Please provide a reason",
          reasonHint: "Required for direct edits.",
          pinned: {
            label: "Pinned",
            tooltip:
              "Pin the task's start date. The scheduler won't auto-shift it when linked tasks move. Use when the date is committed to the customer or stakeholders.",
            indicator: "Pinned: date is fixed",
          },
          fields: {
            name: "Name",
            unit: "Unit",
            unitPlaceholder: "Select a unit",
            plannedStart: "Planned start",
            plannedEnd: "Planned end",
            durationDays: "Duration (work days)",
            effortHours: "Effort (h)",
            defaultPeople: "Default people",
            progressPct: "Progress (%)",
            status: "Status",
            notes: "Notes",
            reason: "Change reason",
          },
        },
      },
    },
    units: {
      title: "Departments and units",
      tabs: {
        structure: "Structure",
      },
      empty: "No units",
      newCta: "Add",
      columns: {
        code: "Code",
        name: "Name",
        alias: "Abbr",
        capacityToday: "Available today",
        sortOrder: "Order",
        status: "Status",
        lead: "Lead",
        employees: "Employees",
      },
      fields: {
        code: "Code",
        name: "Name",
        namePlaceholder: "Welding shop",
        alias: "Abbreviation",
        aliasPlaceholder: "WS",
        capacityToday: "Available today",
        subtreeCapacityToday: "Available with subtree",
        manHours: "{{value}} man-hours",
        sortOrder: "Sort order",
        capacityWarningThreshold: "How much of the team must remain available, %",
        capacityWarningThresholdHint: "For the current unit — at least {{available}} of 10 people.",
        lead: "Lead",
        leadManager: "Reporting manager",
        leadEmpty: "— unassigned —",
        leadClear: "Clear lead",
        parent: "Parent unit",
        parentRoot: "— top level —",
        parentClear: "Clear parent unit",
      },
      tree: {
        expand: "Expand",
        collapse: "Collapse",
        addChild: "Add child",
        rename: "Edit",
        move: "Move to…",
        delete: "Delete",
        searchAria: "Search units",
        searchPlaceholder: "Search by name or abbreviation…",
        youLead: "You lead",
        youDeputize: "You deputize",
        employeeCount_one: "{{count}} employee",
        employeeCount_few: "{{count}} employees",
        employeeCount_many: "{{count}} employees",
        employeeCount_other: "{{count}} employees",
      },
      move: {
        title: "Move “{{name}}”",
        targetLabel: "New parent unit",
        targetPlaceholder: "Select a unit",
        submit: "Move",
        moved: "Unit moved",
        failed: "Could not move the unit",
      },
      delete: {
        title: "Delete “{{name}}”?",
        confirm: "Confirm deletion of this unit. This action cannot be undone.",
        childrenNote_one: "Its child unit ({{count}}) will move up to the parent.",
        childrenNote_other: "Its child units ({{count}}) will move up to the parent.",
        childrenNoteRoot_one: "Its child unit ({{count}}) will become top-level.",
        childrenNoteRoot_other: "Its child units ({{count}}) will become top-level.",
        submit: "Delete",
        deleted: "Unit deleted",
        failed: "Could not delete the unit",
        blockedTitle: "Reassign first",
        blockedTasks_one: "{{count}} task",
        blockedTasks_other: "{{count}} tasks",
        blockedMembers_one: "{{count}} person",
        blockedMembers_other: "{{count}} people",
        blockedHint: "Move them to another unit, then try again.",
      },
      create: { title: "New unit", submit: "Create" },
      edit: { title: "Edit unit", submit: "Save" },
      params: {
        title: "Unit parameters",
        name: "Name",
        shortAlias: "Abbreviation",
        sortOrder: "Sort order",
        threshold: "Team availability threshold",
        lead: "Lead",
        leadPlaceholder: "No lead assigned",
        noLead: "Not assigned",
        dirty: "There are unsaved changes",
        clean: "No changes",
        invalid: "Check this value",
        discardTitle: "Discard changes?",
        discardBody: "Unsaved unit parameter changes will be lost.",
        discardAction: "Discard changes",
      },
      detail: {
        back: "← Back to list",
        edit: "Edit",
        tabs: {
          employees: "Employees",
          availability: "Absences and overtime",
        },
        includeSubtree: "Include child units",
        previousMonth: "Previous month",
        nextMonth: "Next month",
        currentMonth: "Current month",
        horizonLabel: "Planning period",
        horizon_one: "{{count}} month",
        horizon_other: "{{count}} months",
        loadingUnit: "Loading unit…",
        errorUnit: "Could not load the unit.",
        loadingPeople: "Loading employees…",
        errorPeople: "Could not load employees.",
        emptyPeople: "There are no employees in the selected scope.",
        loadingPlanning: "Loading absences and availability…",
        errorPlanning: "Could not load absence planning.",
        emptyPlanning: "There is no planning data in the selected scope.",
        capacityTitle: "Daily availability",
        capacitySubtitle: "{{months}} months ahead",
        capacityHours: "Available, man-hours",
        nominalHours: "Nominal, man-hours",
        peakLabel: "Peak: {{value}}",
        empty: "No data",
        legend: "Grey — day off, orange — calendar override, blue — working day",
        peopleTitle: "Staff",
        viewList: "List",
        viewPeople: "By person",
        scopeOwn: "Direct",
        scopeSubtree: "Subtree",
        horizonMonth: "Month",
        horizonQuarter: "Quarter",
        horizonYear: "Year",
      },
      deputies: {
        title: "Deputies",
        empty: "No deputies",
        permanent: "Permanent",
        until: "through {{date}}",
        userLabel: "Person",
        userPlaceholder: "Select a person",
        untilLabel: "Standing in through",
        untilHint: "Leave empty for a permanent deputy",
        assign: "Assign",
        remove: "Remove",
        removeConfirmTitle: "Remove deputy?",
        removeConfirmBody: "{{deputy}} will no longer be able to act for this unit.",
        assigned: "Deputy assigned",
        removed: "Deputy removed",
        assignFailed: "Couldn't assign the deputy",
        removeFailed: "Couldn't remove the deputy",
        userRequired: "Select a person",
        untilFuture: "The end date must be in the future",
      },
      approvalMode: {
        title: "Time approval",
        daily: "Daily",
        weekly: "Weekly",
        managedByAdmin: "Configured by an administrator",
        hint: "Sets the lead's workspace: daily uses the day-by-day console, weekly uses the week feed.",
        failed: "Couldn't change the approval mode",
      },
      toast: {
        created: "Unit created",
        saved: "Unit updated",
        createFailed: "Failed to create unit",
        saveFailed: "Failed to save",
      },
    },
    availability: {
      gridLabel: "Availability timeline",
      employeeColumn: "Employee",
      capacity: "Team availability",
      teamMemberCount_one: "{{count}} person in the unit",
      teamMemberCount_few: "{{count}} people in the unit",
      teamMemberCount_many: "{{count}} people in the unit",
      teamMemberCount_other: "{{count}} people in the unit",
      allowedAbsenceCount_one: "No highlight: up to {{count}} person absent",
      allowedAbsenceCount_few: "No highlight: up to {{count}} people absent",
      allowedAbsenceCount_many: "No highlight: up to {{count}} people absent",
      allowedAbsenceCount_other: "No highlight: up to {{count}} people absent",
      warningAbsenceCount: "Highlight: {{count}} or more people absent",
      anyAbsenceWarning: "Highlight: any absence",
      previousMonth: "Previous month",
      nextMonth: "Next month",
      currentMonth: "Current month",
      horizonLabel: "Planning horizon",
      horizon_one: "{{count}} month",
      horizon_few: "{{count}} months",
      horizon_many: "{{count}} months",
      horizon_other: "{{count}} months",
      week: "W{{number}}",
      weekCompact: "{{number}}",
      today: "Today",
      dayOff: "Day off",
      capacityValue: "Available: {{available}} of {{total}} person-days",
      tooltip: "{{date}} · {{availability}}",
      absenceIntervalLabel: "{{employee}} · {{kind}} · {{from}} — {{to}}",
      absenceReason: "Reason: {{reason}}",
      absenceHours: "Hours: +{{hours}}",
      eventTypeReason: "Reason: {{type}}",
      eventComment: "Comment: {{reason}}",
      durationDays_one: "Duration: {{count}} day",
      durationDays_few: "Duration: {{count}} days",
      durationDays_many: "Duration: {{count}} days",
      durationDays_other: "Duration: {{count}} days",
      durationHours: "Duration: {{hours}} h",
      loadError: "Could not load the schedule",
      peopleNotFound: "No employees found",
      inactivePerson: "Inactive",
      subtreeCapacityOverview:
        "Overview availability for the selected subtree; child unit thresholds are evaluated separately.",
      editSchedule: "Edit {{name}}'s schedule",
      filters: {
        allUnits: "All units",
        unitSearch: "Find a unit…",
        unitsEmpty: "No units found",
        unitClear: "All units",
        searchAria: "Search by employee name or personnel number",
        searchPlaceholder: "Name or personnel number",
        eventTypes: "Event types: {{selected}} of {{total}}",
        eventTypesSearch: "Search event types…",
        eventTypesEmpty: "No event types found",
      },
      legend: "Legend",
      legendShow: "Show legend",
      legendHide: "Hide legend",
      critical: "Availability below {{threshold}}%",
      absence: {
        vacation: "Vacation",
        unpaid_leave: "Unpaid leave",
        sick: "Sick leave",
        no_show: "No-show",
        day_off: "Day off",
        part_time: "Part-time day",
        overtime: "Overtime",
      },
      empty: "No staff",
      kind: {
        vacation: "Vacation",
        unpaid_leave: "Unpaid leave",
        sick: "Sick leave",
        no_show: "No-show",
        day_off: "Day off",
        part_time: "Part-time",
        overtime: "Overtime",
      },
    },
    people: {
      back: "← Back to unit",
      backUnits: "← Back to units",
      notFound: "Person not found",
      scheduleTitle: "Schedule",
      calendarTitle: "Calendar",
      prevPeriod: "Back",
      nextPeriod: "Forward",
      today: "Today",
      records: "Planned absences and overtime",
      recordsEmpty: "No records",
      showPast: "Show past records",
      addAbsence: "Add absence",
      addOvertime: "Add overtime",
      profileTitle: "Profile",
      profileStatusActive: "Active",
      profileStatusInactive: "Inactive",
      profileDirty: "There are unsaved changes",
      profileClean: "No changes",
      profileDiscardTitle: "Discard changes?",
      profileDiscardBody: "Unsaved profile changes will be lost.",
      profileDiscardAction: "Discard changes",
      workTypesTitle: "Primary work types for the position",
      workTypesInactive: " · retired",
      workTypesLockedHint:
        "Comes with the position. Change the position or its set in the catalog.",
      workTypesAdhocTitle: "Additional work types",
      workTypesAdhocEmpty: "Nothing added beyond the position.",
      workTypesPicker: "Choose work types",
      workTypesSearch: "Search work types…",
      workTypesSaveFailed: "Could not save the work types",
      hours: "{{value}} h",
      payableShort: "paid",
      project: "Project",
      delete: "Delete",
      deleteConfirm: "Delete this schedule record?",
      deleteConfirmBody:
        "The “{{event}}” record for {{from}}–{{to}} belonging to {{person}} will be deleted.",
      leadership: {
        title: "Leadership positions",
        lead: "Manager",
        deputy: "Deputy",
        until: "until {{date}}",
      },
      fields: {
        name: "Staff member",
        position: "Position",
        unit: "Unit",
        calendar: "Base calendar",
        calendarInherited: "Same as company",
        lineManager: "Manager",
        yes: "Yes",
        no: "No",
        none: "—",
      },
      absenceForm: {
        title: "Absence",
        editTitle: "Edit absence",
        desc: "Vacation, sick leave, time off or a part-time day — lowers availability.",
        kind: "Type",
        kindVacation: "Vacation",
        kindUnpaidLeave: "Unpaid leave",
        kindSick: "Sick leave",
        kindNoShow: "No-show",
        kindDayOff: "Day off",
        kindPartTime: "Part-time day",
        dateFrom: "From",
        dateTo: "To",
        hoursOff: "Hours off per day",
        hoursOffHint: "Part-time only; vacation and sick leave zero out the day.",
        reason: "Comment",
        submit: "Add",
      },
      overtimeForm: {
        title: "Overtime",
        editTitle: "Edit overtime",
        desc: "Extra hours on a project — raises availability.",
        kind: "Type",
        project: "Project",
        projectPlaceholder: "Select a project",
        dateFrom: "From",
        dateTo: "To",
        hours: "Hours added per day",
        payable: "Paid",
        reason: "Comment",
        submit: "Add",
      },
      errors: {
        required: "This field is required",
        dateOrder: "End date is before the start date",
        projectRequired: "Overtime needs a project",
        hoursPositive: "Hours must be greater than zero",
        hoursRange: "Invalid number of hours",
      },
      toast: {
        added: "Record added",
        deleted: "Record deleted",
        addFailed: "Could not add the record",
        deleteFailed: "Could not delete the record",
      },
    },
    load: {
      title: "Resource load",
      unit: "Unit",
      unitPlaceholder: "Select a unit",
      from: "From",
      to: "To",
      resetRange: "Reset range",
      capacity: "Available, man-hours",
      allocated: "Load, man-hours",
      empty: "No data for selected range",
      overloadDays_one: "{{count}} overload day",
      overloadDays_few: "{{count}} overload days",
      overloadDays_many: "{{count}} overload days",
      overloadDays_other: "{{count}} overload days",
      drilldown: {
        title: "Per-project breakdown",
        subtitle: "Unit {{unit}} · {{date}}",
        none: "Not selected",
        empty: "No allocations for this day",
        project: "Project",
        people: "People",
        hoursPerPerson: "Hours / person",
        tasks: "Tasks",
        overtime: "overtime",
      },
    },
    portfolio: {
      title: "Portfolio",
      empty: "No tasks to display",
      projectsCount_one: "{{count}} project",
      projectsCount_few: "{{count}} projects",
      projectsCount_many: "{{count}} projects",
      projectsCount_other: "{{count}} projects",
      unitsFilter: "Units",
      clearUnits: "Clear",
      legendMore: "+{{count}} more",
      taskColumn: "Task",
      searchProjects: "Search projects",
      projectsFilter: "Projects",
      clearProjects: "Clear",
      clearStatus: "Clear",
      statusFilter: "Project status",
      tasksFilter: "Tasks",
      searchTasks: "Search tasks",
      clearTasks: "Clear",
    },
    simulation: {
      toast: {
        overtime: "Overtime request submitted for approval",
        taskMove: "Task change submitted for approval",
      },
      help: {
        trigger: "What is a simulation?",
        title: "Change simulation",
        whatIs:
          "A simulation is a proposed change that the system computes cascading effects for (dependencies, unit load) without applying immediately. You see a Gantt diff and decide in /approvals.",
        whenNeeded:
          "When to simulate: shifting planned/actual dates, changing duration, task composition, status — anything that moves another task's schedule or unit load.",
        whenNotNeeded:
          "When not needed: progress, name, notes, tags, pinned date — these are task attributes that don't move the schedule. Saved immediately, no approval.",
        whereLives:
          "Where the result lives: once created, the simulation appears in the bell icon and on /approvals. Apply or reject in one click.",
      },
    },
    approvals: {
      title: "Approvals",
      empty: "No pending simulations",
      count_one: "{{count}} simulation",
      count_few: "{{count}} simulations",
      count_many: "{{count}} simulations",
      count_other: "{{count}} simulations",
      expiresIn: "Expires in {{value}}",
      expired: "Expired",
      timeHM: "{{h}} h {{m}} min",
      timeM: "{{m}} min",
      projectEndChange: "Δ project end (days)",
      affectedTasks: "Affected tasks",
      overloads: "New overloads",
      overloadsMore: "+ {{count}} more",
      project: "Project #{{id}}",
      kindLabel: "Kind",
      apply: "Apply",
      reject: "Reject",
      openGantt: "Open Gantt",
      kind: {
        task_move: "Task move",
        task_status_change: "Status change",
        apply_template: "Apply template",
        create_project_from_template: "New project",
        add_task: "Add task",
        remove_task: "Remove task",
        set_allocation: "Set allocation",
        set_task_overtime: "Set overtime",
        set_company_calendar_override: "Calendar override",
      },
      autoSummary: {
        task_move_one: "Move task «{{task_name}}» by {{count}} day",
        task_move_few: "Move task «{{task_name}}» by {{count}} days",
        task_move_many: "Move task «{{task_name}}» by {{count}} days",
        task_move_other: "Move task «{{task_name}}» by {{count}} days",
        task_status_change: "Status of task «{{task_name}}»: {{old_status}} → {{new_status}}",
        apply_template: "Apply template «{{template_name}}» to project «{{project_name}}»",
        create_project_from_template:
          "New project «{{project_name}}» from template «{{template_name}}» ({{tasks_count}} tasks)",
        add_task: "Add task «{{task_name}}» to project «{{project_name}}»",
        remove_task: "Remove task «{{task_name}}» from project «{{project_name}}»",
        set_allocation: "Allocation of task «{{task_name}}»: {{old_people}} → {{new_people}} ppl",
        set_task_overtime: "Overtime on task «{{task_name}}» ({{date_from}}–{{date_to}})",
        set_company_calendar_override: "Calendar {{date_from}}–{{date_to}}: {{hours}}h/day",
      },
      userCommentLabel: "Comment",
      dialog: {
        applyTitle: "Apply simulation?",
        rejectTitle: "Reject simulation?",
        applyConfirm: "Apply",
        rejectConfirm: "Reject",
        reasonLabel: "Reason (optional)",
        reasonPlaceholder: "Short note for the audit log",
      },
      toast: {
        applied_one: "Applied: {{count}} task affected",
        applied_few: "Applied: {{count}} tasks affected",
        applied_many: "Applied: {{count}} tasks affected",
        applied_other: "Applied: {{count}} tasks affected",
        rejected: "Simulation rejected",
        applyFailed: "Failed to apply simulation",
        rejectFailed: "Failed to reject simulation",
        conflict: "Simulation is stale — refresh and try again",
        expired: "Simulation expired, please create a new one",
      },
      detail: {
        title: "Diff Gantt",
        back: "← Back to list",
        expires: "expires {{when}}",
        modeLabel: "Layer",
        noProject: "Simulation is not bound to a project",
        modes: {
          baseline: "Baseline",
          simulation: "Simulation",
          both: "Both layers",
        },
      },
    },
    risks: {
      title: "Risks",
      empty: "No risks match the selected filters",
      countTotal_one: "{{count}} risk",
      countTotal_few: "{{count}} risks",
      countTotal_many: "{{count}} risks",
      countTotal_other: "{{count}} risks",
      filters: {
        severity: "Severity",
        type: "Type",
        project: "Project",
        all: "All",
        noProject: "No project",
        unresolvedOnly: "Unresolved only",
        includeResolved: "Show resolved",
        reset: "Reset",
        clear: "Clear",
      },
      columns: {
        severity: "Severity",
        type: "Type",
        description: "Description",
        project: "Project",
        detectedAt: "Detected",
        status: "Status",
      },
      severity: {
        critical: "Critical",
        high: "High",
        medium: "Medium",
        low: "Low",
      },
      type: {
        overdue_task: "Overdue task",
        approaching_deadline: "Approaching deadline",
        resource_overload: "Resource overload",
        dangling_dependency: "Dangling dependency",
        critical_path_slippage: "Critical path slip",
      },
      status: {
        active: "active",
        resolved: "resolved",
      },
      resolvedAt: "resolved {{when}}",
      noProject: "—",
      noProjectChip: "No project",
      help: {
        aria: "About risks",
        title: "What risks the system tracks",
        intro:
          "Gudwin periodically scans projects and highlights situations that need attention. Severity grows with how serious the issue is.",
        detectors: {
          overdue_task:
            "an active task whose planned end is in the past. Severity grows with how many days late it is: 1–2 days → low, 3–6 days → medium, 7–13 days → high, 14+ days → critical.",
          approaching_deadline:
            "an active task is due within the next 5 days. Severity: 4–5 days → low, 2–3 days → medium, 0–1 days → high. Once the deadline is passed, the task switches to “Overdue task”.",
          resource_overload:
            "a unit’s scheduled load exceeds its capacity in some period. Severity grows with the overload ratio: ×1.00–1.24 → low, ×1.25–1.49 → medium, ×1.50–1.99 → high, ×2.0+ → critical.",
          dangling_dependency:
            "the predecessor in a task dependency was cancelled while the dependent task is still active. Severity is always medium — either cancel the dependent task, or remove or rewire the dependency.",
        },
        refresh:
          "Risk detection currently runs on demand. Once a regular auto-refresh is implemented this note will be updated.",
        usage:
          "Simulations and direct edits can clear a risk — it will disappear from the active list on the next refresh, or remain in history as resolved.",
      },
    },
    audit: {
      title: "Audit log",
      empty: "No entries match the selected filters",
      loadMore: "Load more",
      loading: "Loading…",
      countLoaded_one: "{{count}} entry",
      countLoaded_few: "{{count}} entries",
      countLoaded_many: "{{count}} entries",
      countLoaded_other: "{{count}} entries",
      tabs: {
        plan: "Plan changes",
      },
      filters: {
        project: "Project",
        actor: "Actor",
        actorPlaceholder: "Username",
        kind: "Kind",
        dateFrom: "From",
        dateTo: "To",
        all: "All",
        noProject: "No project",
        reset: "Reset",
      },
      kind: {
        task_move: "Task move",
        task_status_change: "Status change",
        apply_template: "Template apply",
        add_task: "Task added",
        remove_task: "Task removed",
        set_allocation: "Allocation",
        set_task_overtime: "Overtime",
        set_company_calendar_override: "Calendar override",
        create_project_from_template: "Project created",
        manual_edit: "Manual edit",
        // Task 18 §2.2 — security feed. Nested to match i18next's default
        // keySeparator='.' so `t('audit.kind.auth.login.success')` resolves
        // to the leaf string instead of an intermediate object.
        auth: {
          login: {
            success: "Login",
            failed: "Failed login",
          },
          account: {
            locked: "Account locked",
          },
          pin: {
            locked: "PIN locked",
          },
          password: {
            changed: "Password changed",
          },
        },
        admin: {
          user: {
            create: "User created",
            patch: "Staff member updated",
            deactivate: "User deactivated",
            reset_pin: "PIN reset",
            work_types: "Work types changed",
          },
        },
      },
      securityPreset: "Security",
      openGantt: "Open Gantt",
      presentation: {
        unknownEvent: "Unknown event",
        unknownObject: "Object not identified",
        unknownActor: "Actor not identified",
        systemActor: "System",
        showDetails: "Show details",
        hideDetails: "Hide details",
        changeCount_one: "{{count}} change",
        changeCount_few: "{{count}} changes",
        changeCount_many: "{{count}} changes",
        changeCount_other: "{{count}} changes",
        reason: "Reason",
        columns: {
          details: "Details",
          when: "When",
          actor: "Actor",
          event: "Event",
          object: "Object",
          changes: "Changes",
          field: "Field",
          before: "Before",
          after: "After",
        },
        operation: {
          create: "Created",
          update: "Updated",
          delete: "Deleted",
        },
      },
      timesheet: {
        title: "History",
        subtitle: "Raw shift-report entries feed.",
        forbidden: "Available to the unit lead and administrators.",
        empty: "No entries match the selected filters",
        loadMore: "Load more",
        loading: "Loading…",
        filters: {
          worker: "Worker",
          project: "Project",
          workType: "Work",
          dateFrom: "Shift from",
          dateTo: "Shift to",
          all: "All",
          showDeleted: "Show deleted",
          needsAttention: "Needs attention only",
          needsAttentionHelp: {
            title: "What counts as needing attention",
            items: [
              "Project not recognized",
              "Entry without a project",
              "Work type not recognized",
              "Timesheet differs from daily norm",
              "Rework without an identifier",
            ],
          },
          adminBackfillOnly: "Admin backfill only",
          reset: "Reset",
        },
        columns: {
          entryId: "#",
          when: "When",
          worker: "Worker",
          workDate: "Shift date",
          project: "Project",
          work: "Work",
          hours: "Hours",
          declared: "Timesheet",
          status: "Status",
          timeToSign: "Time to submit",
          source: "Source",
          actions: "",
        },
        openInBackfill: "Open in time tracking",
        unknownProject: "Unknown project",
        noProject: "No project",
        skippedProject: "Entry without a project — confirm this is intentional.",
        unknownWorkType: "Unknown work type",
        declaredOffNorm: "Timesheet differs from daily norm",
        reworkUnlabeled: "Rework without an identifier",
        reworkUnlabeledHint: "Hours logged against a closed work item — add a rework identifier.",
        replaced: "REPLACED",
        deleted: "DELETED",
        adminBackfill: "by admin",
        adminBackfillUnknown: "admin",
        replacedBy: "→ replaced by entry #{{id}}",
        replacedTooltip: "Re-submitted by worker, replaced by entry #{{id}}",
        replacedTooltipNoRef: "Re-submitted by worker, replaced by a new entry",
        userDeletedTooltip: "Deleted by worker",
        userDeletedAt: "Deleted by worker · {{at}}",
        adminDeletedTooltip: "Deleted by admin",
        adminDeletedAt: "Deleted by admin · {{at}}",
        deletedBy: "deleted · {{at}}",
        signedAt: "submitted {{at}}",
        approvedAt: "approved {{at}}",
        timeToSign: "to submit · {{value}}",
        status: {
          draft: "draft",
          signed: "submitted",
          approved: "approved",
        },
        hoursSuffix: "h",
        tab: "tab. {{value}}",
        loadError: "Failed to load feed",
      },
    },
    calendar: {
      title: "Company production calendar",
      subtitle:
        "We follow the Russian Federal Labor Code by default. You can override — add days off, working days, or change working hours.",
      calendarSelect: "Calendar",
      defaultName: "Standard",
      standardWeek: "Default standard week",
      standardWeekHours: "{{day}} · {{hours}} h",
      standardWeekOff: "{{day}} · day off",
      weekDayShort: {
        mon: "Mon",
        tue: "Tue",
        wed: "Wed",
        thu: "Thu",
        fri: "Fri",
        sat: "Sat",
        sun: "Sun",
      },
      weekDayFull: {
        mon: "Monday",
        tue: "Tuesday",
        wed: "Wednesday",
        thu: "Thursday",
        fri: "Friday",
        sat: "Saturday",
        sun: "Sunday",
      },
      empty: "No overrides",
      newCta: "Add override",
      forbidden: "Read-only — only a project manager or admin can change overrides.",
      columns: {
        dateFrom: "From",
        dateTo: "To",
        hours: "Hours",
        reason: "Reason",
        createdBy: "Author",
        createdAt: "Created",
        actions: "Actions",
      },
      fields: {
        dateFrom: "Date from",
        dateTo: "Date to",
        hours: "Hours (0 = day off, 1–7 = short day, 8 = standard, >8 = overtime)",
        reason: "Reason",
        editReason: "Edit reason (for audit log)",
      },
      hoursHint: {
        zero: "holiday / day off",
        short: "short day",
        normal: "standard working",
        overtime: "extended working",
      },
      create: { title: "New override", submit: "Create" },
      edit: { title: "Edit override", submit: "Save" },
      delete: {
        confirmTitle: "Delete override?",
        confirmBody:
          "The {{hours}} h override from {{from}} to {{to}} will be deleted permanently.",
        confirm: "Delete",
        cancel: "Cancel",
      },
      errors: {
        required: "Required",
        dateOrder: "Date `to` must be ≥ `from`",
        hoursRange: "Hours — integer between 0 and 24",
      },
      toast: {
        created: "Override created",
        saved: "Override updated",
        deleted: "Override deleted",
        createFailed: "Failed to create",
        saveFailed: "Failed to save",
        deleteFailed: "Failed to delete",
      },
    },
    admin: {
      catalogs: {
        title: "Catalogs",
        subtitle: "Shared reference books for projects and time tracking.",
        tabs: {
          workTypes: "Work types",
          equipmentTypes: "Equipment types",
          assemblyElements: "Equipment elements",
          personEventTypes: "Absence and overtime types",
          positions: "Positions",
          calendar: "Production calendar",
        },
      },
      personEventTypes: {
        newCta: "Create type",
        empty: "The type catalog is empty. Create the first type.",
        noResults: "Nothing found.",
        searchPlaceholder: "Search by name, code, or abbreviation…",
        searchAria: "Search absence or overtime type",
        selectPrompt: "Select a type on the left or create a new one.",
        createTitle: "New type",
        editTitle: "Type details",
        create: "Create",
        save: "Save",
        delete: "Delete",
        dragHandle: "Drag to reorder",
        lockReason:
          "This type is already in use. Its name, group, abbreviation, and code are protected; color, status, and order can still change.",
        columns: {
          name: "Name",
          group: "Group",
          abbreviation: "Abbr.",
          code: "Code",
          status: "Status",
        },
        fields: {
          name: "Name",
          group: "Group",
          color: "Color",
          abbreviation: "Abbreviation",
          code: "Code",
        },
        groups: { absence: "Absence", overtime: "Overtime" },
        colors: {
          gray: "Gray",
          dark: "Graphite",
          blue: "Blue",
          yellow: "Yellow",
          green: "Green",
          red: "Red",
        },
        deleteDialog: {
          title: "Delete “{{name}}”?",
          body: "Type “{{name}}” will be removed from the catalog. This action cannot be undone.",
        },
        errors: { nameRequired: "Enter a name" },
        toast: {
          saved: "Saved",
          saveFailed: "Failed to save",
          reorderFailed: "Failed to reorder",
        },
      },
      positions: {
        newCta: "Add position",
        empty: "The positions catalog is empty. Create the first position.",
        noResults: "Nothing found.",
        searchPlaceholder: "Search position…",
        searchAria: "Search by position name",
        selectPrompt: "Pick a position on the left to build its work type set.",
        deleteAction: "Delete",
        cancel: "Cancel",
        create: "Create",
        row: {
          dragHandle: "Drag to reorder",
          editHint: "Double-click to rename",
        },
        fields: {
          name: "Name",
          namePlaceholder: "Fitter",
        },
        workTypes: {
          paneLabel: "Work type set",
          listLabel: "In the position set",
          readOnlyHint: "Work type assignments are edited on the Work types tab.",
        },
        createDialog: {
          title: "New position",
        },
        deleteDialog: {
          title: "Delete “{{name}}”?",
          body: "Position “{{name}}” will be deleted permanently.",
          submit: "Delete",
        },
        deleteBlockedDialog: {
          title: "Cannot delete “{{name}}”",
          body: "The position is assigned to employees. Reassign them or deactivate the position — it then disappears from the picker while existing assignments stay intact.",
          ok: "Got it",
        },
        errors: {
          nameRequired: "Enter a name",
        },
        toast: {
          created: "Position created",
          updated: "Saved",
          deleted: "Deleted",
          createFailed: "Failed to create",
          updateFailed: "Failed to save",
          deleteFailed: "Failed to delete",
          reorderFailed: "Failed to reorder",
          workTypesFailed: "Failed to save the work type set",
        },
      },
      equipmentTypes: {
        newCta: "Create type",
        empty: "The equipment types catalog is empty. Create the first type.",
        noResults: "Nothing found.",
        noCategory: "No category",
        searchPlaceholder: "Search equipment type…",
        searchAria: "Search by equipment type name",
        selectPrompt: "Select an equipment type on the left to set its composition.",
        status: {
          active: "ACTIVE",
          inactive: "OFF",
        },
        row: {
          dragHandle: "Drag to reorder",
          editHint: "Double-click to rename",
        },
        editAction: "Edit",
        deleteAction: "Delete",
        cancel: "Cancel",
        save: "Save",
        create: "Create",
        fields: {
          name: "Name",
          namePlaceholder: "Column lift",
          category: "Category",
          categoryPlaceholder: "For example, column",
          categoryHint: "Already used:",
        },
        createDialog: {
          title: "New equipment type",
        },
        editDialog: {
          title: "Edit equipment type",
        },
        renameDialog: {
          title: "Rename “{{name}}”",
        },
        deleteDialog: {
          title: "Delete “{{name}}”?",
          body: "Equipment type “{{name}}” and its composition will be deleted permanently.",
          submit: "Delete",
        },
        deleteBlockedDialog: {
          title: "Cannot delete “{{name}}”",
          body: "The equipment type is in use. It cannot be deleted — uncheck “Active” to hide it from selection.",
          ok: "Got it",
        },
        errors: {
          nameRequired: "Enter a name",
        },
        elements: {
          listLabel: "Composition",
          searchPlaceholder: "Search element…",
          searchAria: "Search by element name or category",
          empty: "The elements catalog is empty. Add the first element.",
          noResults: "Nothing found.",
          noComposition: "This type has no composition. Turn off Hide absent to add elements.",
          hideAbsent: "Hide absent",
          searchRevealNotice: "Search shows all matches",
          addCta: "Create element",
          partialLinkFailure:
            "The element was created but not added to the composition. Retry linking it.",
          retryLink: "Retry linking",
          addDialogTitle: "New element",
          addNameLabel: "Element name",
          addNamePlaceholder: "Frame",
          statusAria: "Status of element “{{name}}” in the type composition",
          unlinkConfirmTitle: "Remove element from composition?",
          unlinkConfirmBody:
            "Element “{{element}}” will be removed from equipment type “{{type}}”.",
          status: {
            none: "No",
            required: "Yes",
            optional: "Option",
          },
        },
        toast: {
          created: "Equipment type created",
          updated: "Saved",
          deleted: "Deleted",
          statusUpdated: "Composition updated",
          elementCreated: "Element added",
          createFailed: "Could not create",
          updateFailed: "Could not save",
          deleteFailed: "Could not delete",
          reorderFailed: "Could not reorder",
          statusFailed: "Could not update composition",
        },
      },
      assemblyElements: {
        newCta: "Create element",
        empty: "The elements catalog is empty. Create the first element.",
        noResults: "Nothing found.",
        noCategory: "No category",
        searchPlaceholder: "Search element…",
        searchAria: "Search by element name or category",
        selectPrompt: "Select an element on the left to set its parameters.",
        status: {
          active: "ACTIVE",
          inactive: "OFF",
        },
        row: {
          dragHandle: "Drag to reorder",
          editHint: "Double-click to rename",
        },
        deleteAction: "Delete",
        editAction: "Edit",
        cancel: "Cancel",
        save: "Save",
        create: "Create",
        fields: {
          name: "Name",
          category: "Category",
          categoryPlaceholder: "For example, structural elements",
          categoryHint: "Already used:",
        },
        createDialog: {
          title: "New element",
        },
        editDialog: {
          title: "Edit element",
        },
        deleteDialog: {
          title: "Delete “{{name}}”?",
          body: "Element “{{name}}” and its parameters will be deleted permanently.",
          submit: "Delete",
        },
        deleteBlockedDialog: {
          title: "Cannot delete “{{name}}”",
          body: "The element is part of one or more equipment types. It cannot be deleted — uncheck “Active” to hide it from selection.",
          ok: "Got it",
        },
        errors: {
          nameRequired: "Enter a name",
        },
        metrics: {
          listLabel: "Parameters",
          empty: "This element has no parameters yet. Add the first one.",
          addCta: "Add parameter",
          deleteAction: "Delete parameter",
          optionsCta: "Options · {{count}}",
          type: {
            number: "Number",
            choice: "Choice",
            text: "Text",
          },
          fields: {
            name: "Name",
            type: "Type",
            typePlaceholder: "Select a type",
            typeHint: "The type cannot be changed after creation.",
          },
          createDialog: {
            title: "New parameter",
          },
          deleteDialog: {
            title: "Delete parameter “{{name}}”?",
            body: "Parameter “{{name}}” and its options will be deleted permanently.",
            submit: "Delete",
          },
          optionsDialog: {
            title: "Options for parameter “{{name}}”",
            empty: "No options yet. Add the first one.",
            addCta: "Add option",
            valuePlaceholder: "For example, Standard",
            removeAria: "Remove option",
            removeConfirmTitle: "Delete option?",
            removeConfirmBody: "Option “{{name}}” will be removed from the draft permanently.",
            emptyValue: "Untitled",
          },
        },
        toast: {
          created: "Element created",
          updated: "Saved",
          deleted: "Deleted",
          metricCreated: "Parameter added",
          metricDeleted: "Parameter deleted",
          optionsUpdated: "Options saved",
          createFailed: "Could not create",
          updateFailed: "Could not save",
          deleteFailed: "Could not delete",
          reorderFailed: "Could not reorder",
          optionsFailed: "Could not save options",
        },
      },
      users: {
        title: "Employees",
        subtitle: "Admin-only. Create / edit / deactivate users.",
        subtitleLead: "Staff of your unit. Create, edit, or deactivate.",
        empty: "No employees found",
        newCta: "Add employee",
        statusActive: "active",
        statusInactive: "inactive",
        tracksTime: {
          on: "Tracks time",
        },
        columns: {
          person: "Employee",
          role: "Role",
          unit: "Unit",
          status: "Status",
          lastLogin: "Last login",
          actions: "Actions",
          position: "Position",
        },
        fields: {
          last_name: "Last name",
          first_name: "First name",
          patronymic: "Patronymic",
          role: "Role",
          unit: "Unit",
          email: "Email",
          tracksTime: "Tracks time",
          projectManager: "Project manager",
          tabNumber: "Tab number",
          tabNumberOptional: "Tab number (optional)",
          tabNumberPlaceholder: "e.g. 1024",
          initialPin: "Initial PIN",
          position: "Position",
          positionEmptyValue: "Not set",
          positionSearchPlaceholder: "Search positions…",
          positionNotFound: "No position found",
          positionClear: "Clear the position",
          positionCreate: 'Create position "{{name}}"',
          positionHint:
            "Work types come from the position. Extra ones are set on the person's card.",
          unitPlaceholder: "Select a unit",
          unitLockedHint: "Your unit — cannot be changed.",
          schedule: "Work schedule",
          scheduleDefault: "Same as company",
          scheduleHint: "A person's permanent schedule. Defaults to the company work schedule.",
          hireDate: "Hire date",
          terminationDate: "Termination date",
        },
        searchAria: "Search by name or tab number",
        searchPlaceholder: "Search by name or tab number…",
        unitFilter: {
          placeholder: "All units",
          searchPlaceholder: "Search unit…",
          empty: "No unit found",
          clear: "Clear unit filter",
        },
        positionFilter: {
          placeholder: "All positions",
          searchPlaceholder: "Search positions…",
          empty: "No position found",
          clear: "Clear position filter",
        },
        filtersReset: "Reset filters",
        activity: {
          label: "Activity",
          active: "Active",
          inactive: "Inactive",
          all: "All",
        },
        groupSwitcher: {
          label: "User type",
          office: "Office staff",
          worker: "Production worker",
        },
        actions: {
          resetPin: "Reset PIN",
          resetPassword: "Reset password",
          edit: "Edit",
          reactivate: "Reactivate",
        },
        actionReasons: {
          self_deactivation: "You cannot deactivate your own account",
          last_active_admin: "The last active administrator cannot be deactivated",
          inactive_account: "The account is inactive",
          active_account: "The account is already active",
          outside_management_scope: "This employee is outside your management scope",
          protected_role: "You cannot manage this role",
          wrong_auth_method: "This action does not apply to the employee's sign-in method",
        },
        create: { title: "New employee", submit: "Create" },
        edit: { title: "Edit user", submit: "Save" },
        deactivate: "Deactivate",
        deactivateDialog: {
          title: "Deactivate employee?",
          body: "Access for {{name}} will be revoked. Their data and history will remain in the system.",
        },
        unitNone: "—",
        tempPasswordDialog: {
          title: "User created",
          subtitle:
            "Copy the password and hand it over to the new user — once you close this dialog the password cannot be seen again.",
          copy: "Copy",
          copied: "Copied",
          done: "Done",
        },
        reactivateDialog: {
          title: "Reactivate user?",
          body: "Reactivate user {{name}}?",
        },
        resetPinConfirm: {
          title: "Reset employee PIN?",
          body: "The current PIN for {{name}} will stop working. A new one will be generated and shown once.",
          submit: "Reset PIN",
        },
        resetPinDialog: {
          title: "New employee PIN",
          subtitle: "{{name}} (tab {{tab}}). The old PIN is revoked and lockout is cleared.",
          warning:
            "The PIN is shown once. Copy it and hand it to the employee before closing — it cannot be recovered.",
          copy: "Copy",
          copied: "Copied",
          done: "Done",
        },
        resetPasswordConfirm: {
          title: "Reset employee password?",
          body: "The current password for {{name}} will stop working and open sessions will end. A temporary password will be generated and shown once.",
          submit: "Reset password",
        },
        resetPasswordDialog: {
          title: "New employee password",
          subtitle: "{{name}}. The old password is revoked and sessions are ended.",
          warning:
            "The password is shown once. Copy it and hand it to the employee before closing — it cannot be recovered.",
          copy: "Copy",
          copied: "Copied",
          done: "Done",
        },
        errors: {
          required: "Required",
          emailFormat: "Invalid email",
          unitRequired: "Select a unit",
          tabFormat: "Tab number must be 3-6 digits",
          pinFormat: "PIN must be exactly 4 digits",
          employmentDateOrder: "Termination date cannot be earlier than hire date",
          projectManagerRole: "Turn off the project-manager flag before changing the role",
        },
        toast: {
          created: "User created",
          workerCreated: "Production worker created",
          positionCreateFailed: "Failed to create the position",
          saved: "User updated",
          deactivated: "User deactivated",
          reactivated: "User reactivated",
          createFailed: "Failed to create",
          saveFailed: "Failed to save",
          deactivateFailed: "Failed to deactivate",
          reactivateFailed: "Failed to reactivate",
          resetPinFailed: "Failed to reset PIN",
          resetPasswordFailed: "Failed to reset password",
        },
        guards: {
          selfDeactivate: "Cannot deactivate your own account",
          selfRoleAdmin: "Cannot remove admin role from your own account",
          lastAdmin: "Cannot deactivate the last active admin",
          lastAdminRole: "Cannot remove admin role from the last active admin",
        },
      },
      checklist: {
        title: "Checkpoints",
        paneAria: "Work type checkpoints",
        pickWorkType: "Pick a work type on the left to set up its checkpoints",
        empty:
          "This work type has no checkpoints yet. Add the first one — it will apply to new works.",
        addCta: "Add checkpoint",
        createDialogTitle: "New checkpoint",
        titleLabel: "Title",
        titleRequired: "Enter a title",
        create: "Add",
        editHint: "Double-click to rename",
        dragAria: "Move checkpoint",
        deleteAria: "Delete checkpoint",
        activeF: "ACTIVE",
        inactiveF: "OFF",
        deleteConfirmTitle: "Delete this checkpoint from the catalog?",
        deleteConfirmBody:
          "“{{title}}” will stop applying to new works. Works that already have it keep the checkpoint and its completion mark.",
        toast: {
          created: "Checkpoint added",
          createFailed: "Failed to add the checkpoint",
          updated: "Checkpoint updated",
          updateFailed: "Failed to update the checkpoint",
          deleted: "Checkpoint deleted",
        },
        condition: {
          none: "No conditions — applies to every work of this type",
          addCta: "Add condition",
          equipment: "Equipment type",
          element: "Element",
          anyEquipment: "Any equipment type",
          anyElement: "Any element",
          saveCta: "Add",
          clear: "Clear",
          notFound: "Nothing found",
          removeAria: "Remove condition",
          hint: "A checkpoint applies to a work if the project matches at least one condition. Inside one condition, “+” means both must match.",
          removeConfirmTitle: "Remove this condition?",
          removeConfirmBody:
            "“{{condition}}” will stop applying: the checkpoint will start landing in works that do not match it.",
        },
      },
      workTypes: {
        title: "Work types",
        subtitle: "Catalog of work types shown to workers on shift entry.",
        newCta: "Create work type",
        empty: "Catalog is empty. Create the first work type.",
        searchPlaceholder: "Search work type…",
        searchAria: "Search by work type name",
        noResults: {
          active: "No active work types match.",
          all: "No work types match.",
        },
        activity: {
          label: "Activity",
          active: "Active",
          all: "All",
        },
        noCategory: "No category",
        positions: {
          title: "Available to positions",
          more: "{{count}} more",
          edit: "Edit",
          search: "Search position…",
          searchEmpty: "No matches",
          empty: "Not available to any positions",
          active: "ACTIVE",
          inactive: "OFF",
          saveFailed: "Failed to save positions",
        },
        selectPrompt: "Pick a work type on the left to view its task types.",
        sortHint: "Use the arrows to reorder.",
        listLabel: "Work types",
        detailLabel: "Task types",
        statusActive: "active",
        statusInactive: "inactive",
        active: "Active",
        status: {
          active: "ACTIVE",
          inactive: "OFF",
        },
        row: {
          dragHandle: "Drag to reorder",
          editHint: "Double-click to rename",
        },
        moveUp: "Move up",
        moveDown: "Move down",
        rename: "Rename",
        editAction: "Edit",
        deleteAction: "Delete",
        cancel: "Cancel",
        save: "Save",
        create: "Create",
        fields: {
          name: "Name",
          namePlaceholder: "Welding",
          category: "Category",
          categoryPlaceholder: "For example, production",
          categoryHint: "Already used:",
        },
        createDialog: { title: "New work type" },
        editDialog: { title: "Edit work type" },
        renameDialog: { title: "Rename “{{name}}”" },
        deleteDialog: {
          title: "Delete “{{name}}”?",
          body: "Work type “{{name}}” will be deleted permanently.",
          submit: "Delete",
        },
        deleteBlockedDialog: {
          title: "Cannot delete “{{name}}”",
          body: "This entry is referenced by shift reports. Uncheck “Active” to hide it from new reports instead.",
          ok: "Got it",
        },
        errors: {
          nameRequired: "Enter a name",
          nameDuplicate: "A work type with this name already exists",
        },
        toast: {
          created: "Work type created",
          updated: "Saved",
          deleted: "Deleted",
          createFailed: "Failed to create",
          updateFailed: "Failed to save",
          deleteFailed: "Failed to delete",
          reorderFailed: "Failed to reorder",
        },
      },
      backfill: {
        kicker: "Time tracking",
        title: "Employee time tracking",
        subtitle: "Fill in a missed shift for the chosen date or edit an existing one.",
        forbidden: "Admins and unit leads only.",
        loadError: "Failed to load timesheet.",
        emptyHint: "Pick an employee and a date to open the shift.",
        cancel: "Cancel",
        fields: {
          worker: "Employee",
          workerPlaceholder: "— select employee —",
          workersError: "Failed to load employees.",
          date: "Date",
          today: "Today",
        },
        status: {
          draft: "draft",
          signed: "submitted",
          approved: "approved",
        },
        entries: {
          empty: "No entries for this date yet.",
          col: {
            project: "Project",
            workType: "Work",
            hours: "Hours",
          },
          delete: "Delete",
          edit: "Edit entry",
          deleted: "Entry deleted",
          deleteFailed: "Failed to delete",
          deleteConfirmTitle: "Delete entry?",
          deleteConfirmOk: "Delete",
        },
        composer: {
          title: "Add entry",
          workType: "Work",
          workTypePlaceholder: "— select —",
          workTypeFree: "Free-text…",
          workTypeFreePlaceholder: "Describe the work",
          project: "Project",
          projectSkip: "No project",
          hours: "Hours",
          submit: "Record",
          created: "Entry added",
          createFailed: "Failed to save",
        },
        project: {
          placeholder: "— select project —",
          searchPlaceholder: "Project code or name",
          hint: "Enter a code or a name.",
          empty: "No matches.",
          unknownTag: "off-register",
          recordAsIs: "Record «{{code}}» as-is",
          clearAria: "Clear project",
          confirmTitle: "Project off the register",
          confirmBody:
            "There is no such project on the register. Record code «{{code}}» as-is? A supervisor can map it later.",
          confirmCancel: "Cancel",
          confirmOk: "Record as-is",
        },
        sign: {
          sumLabel: "Entries total",
          declaredLabel: "Timesheet hours",
          cta: "Submit",
          confirmTitle: "Submit report?",
          confirmBody: "Submit the report for {{name}} on {{date}}?",
          confirmOk: "Submit",
          toast: "Report submitted",
          failed: "Failed to submit report",
        },
      },
    },
    field: {
      shell: {
        logout: "Sign out",
        context: {
          timesheet: "Shift",
          sign: "Report",
        },
        placeholder: {
          timesheet: "Loading",
          sign: "Shift report",
        },
      },
      login: {
        title: "Employee number",
        subtitle: "3 to 6 digits",
        backToOffice: "Email sign-in",
        digitsAria: "Entered {{filled}} digits",
        errors: {
          invalidCredentials: "Wrong employee number or PIN",
          accountDisabled: "Account disabled. Contact your supervisor.",
          networkError: "Network error, please try again",
        },
      },
      logout: "Sign out",
      titles: {
        today: "Today",
        week: "This week",
        overdue: "Overdue",
      },
      tabs: {
        today: "Today",
        week: "Week",
        overdue: "Overdue",
      },
      empty: {
        today: "No tasks for today",
        week: "No tasks scheduled for the next week",
        overdue: "No overdue tasks",
      },
      offline: {
        banner: "You're offline. Actions are paused, comments are saved locally.",
        toast: {
          savedLocally: "Saved locally — we'll submit it once you're back online",
          dropped: "Failed to submit a queued entry after reconnect",
        },
      },
      card: {
        dates: "Dates",
        status: "Status",
        progress: "Progress",
      },
      status: {
        planned: "Planned",
        in_progress: "In progress",
        completed: "Completed",
        blocked: "Blocked",
        cancelled: "Cancelled",
      },
      actions: {
        start: "Start",
        finish: "Finish",
        comment: "Comment",
      },
      comment: {
        title: "Task comment",
        placeholder: "Anything to log?",
        offlineHint:
          "You're offline — the comment is saved locally and you can submit it once you're back online.",
      },
      pin: {
        title: "PIN",
        context: {
          login: "Sign in",
          sign: "Submit report",
        },
        submitting: "Checking…",
        dotsAria: "Entered {{filled}} of {{length}}",
        error: {
          attempt: "Wrong PIN · attempt {{attempt}} of {{max}}",
          invalid: "Wrong PIN",
        },
        lockout: {
          message: "Account locked. Contact your supervisor.",
          close: "Close",
        },
      },
      toast: {
        started: "Marked as started",
        finished: "Marked as finished",
        commentSaved: "Comment saved",
        actionFailed: "Action failed",
      },
      main: {
        date: {
          today: "Today",
          yesterday: "Yesterday",
          dayBefore: "Two days ago",
          changeAria: "Change date",
        },
        backfill: {
          label: "Late entry",
          days_one: "day ago",
          days_few: "days ago",
          days_many: "days ago",
          days_other: "days ago",
        },
        dateSheet: {
          title: "Which day?",
        },
        stat: {
          totalWorks: "Total work",
          norm: "Per timesheet {{hours}}",
        },
        entries: {
          empty: "No entries yet",
          editAria: "Edit entry",
          deleteAria: "Delete entry",
          deleteTitle: "Delete entry?",
          deleteBody: "The entry will be removed from this shift. This cannot be undone.",
          deleteOk: "Delete",
          deleteCancel: "Cancel",
          deleteToastFailed: "Failed to delete entry",
          unknownProject: "not in system",
          noProject: "No project",
        },
        emptyState: {
          title: "Nothing recorded yet",
          subtitle: "Tap the button below to add your first work entry",
          hint: "start here ↓",
        },
        trackingOff: {
          title: "Time tracking is off",
          subtitle: "Your time is not being tracked right now. Contact your manager.",
        },
        addEntry: "Add work",
        addMore: "Add another work",
        addMoreHint: "all done? then «submit»",
        total: "Total",
        sign: "Submit",
        signDisabledHint: "Add at least one entry first",
        delete: {
          title: "Delete entry?",
          description: "Entry and hours will be removed from the report.",
          confirm: "Delete",
          cancel: "Cancel",
        },
        toast: {
          entryDeleted: "Entry deleted",
          entryDeleteFailed: "Failed to delete entry",
        },
        error: {
          backfillWindow: "Too far back to edit. Contact your supervisor.",
        },
        readonly: {
          signed: "Report submitted",
          approved: "Approved by supervisor",
          editHint: "Changes immediately become part of the submitted report.",
          edit: "Edit report",
        },
      },
      entryFlow: {
        stepCounter: "Step {{current}} of {{total}}",
        editBadge: "Edit entry",
        dockTitle: "Enter time record",
        dockDescription: "Work, project and hours for the selected day.",
        closeAria: "Close",
        cancel: "Cancel",
        continue: "Next",
        saving: "Saving…",
        submitCta: "Save · {{hours}}",
        closeConfirmTitle: "Close without saving?",
        closeConfirmBody: "The entered data will be lost.",
        closeConfirmCancel: "Back",
        closeConfirmOk: "Close",
        deleteAria: "Delete entry",
        deleteTitle: "Delete entry?",
        deleteBody: "Entry and hours will be removed from the report.",
        deleteBodyNamed: "Entry “{{name}}” and its hours will be removed from the report.",
        deleteOk: "Delete",
        pill: {
          workType: "work",
          project: "project",
          projectSkipped: "no project",
          element: "element",
          elementSkipped: "no element",
          elementFallback: "element",
          clearAria: "Change step",
        },
        step1: {
          title: "Work",
          customCta: "Enter custom work",
          customLabel: "Custom work",
          customPlaceholder: "E.g. loading metal stock",
        },
        step2: {
          title: "Project number",
          recentHint: "Recent",
          manualCta: "Enter number manually",
          searchHint: "Enter at least one digit of the project number",
          emptyShort: "No matches — try a different number.",
          recordAsIs: "Save as is",
          padAria: "Project number digits",
          unknownProjectTitle: "Project not in the system",
          unknownProjectBody:
            "Project «{{code}}» is not found. Check the number. If you're sure — save as is, supervisor will sort it out.",
          unknownProjectCancel: "Check again",
          unknownProjectOk: "Save as is",
          skipCta: "No project",
        },
        step3: {
          title: "Element",
          loading: "Loading elements…",
          empty: "This project has no elements",
          skipCta: "No element",
          skipEmptyCta: "Continue without element",
        },
        step4: {
          title: "How many hours?",
          hint: "0.5h step · from 0.5 to 24",
          required: "Enter the number of hours",
        },
        rework: {
          badge: "Rework",
          bannerTitle: "This is rework",
          bannerBody: "The work was closed on {{date}}. Note what you're reworking.",
          bannerBodyNoDate: "The work was closed earlier. Note what you're reworking.",
          noteLabel: "Rework label",
          notePlaceholder: "Rework label — e.g. QC remark",
        },
        toast: {
          saveFailed: "Failed to save entry",
          deleteFailed: "Failed to delete entry",
          conflict: "Entry was changed, refresh the screen",
        },
      },
      sign: {
        loading: "Loading report",
        back: {
          aria: "Back to entries",
        },
        summary: {
          title: "Report summary",
          total: "Total work",
          empty: "No entries — you can still submit; hours will go to «Untracked».",
        },
        declared: {
          title: "Timesheet",
          edit: "edit",
        },
        tabelEdit: {
          title: "Timesheet for this shift",
          calendarHint: "By production calendar — {{hours}}",
          overtimeBadge: "Overtime +{{hours}} h",
          overtimeBody: "Raising the declared total above the norm will need management approval.",
          cancel: "Cancel",
          apply: "Apply",
          confirmOvertime: "Confirm overtime",
          stepperAria: "Declared hours",
        },
        button: "Submit",
        pinTitle: "Submit report",
        confirm: {
          title: "Submit report",
          body: "The report will be submitted in your name.",
          submitting: "Submitting…",
        },
        toast: {
          submitted: "Report submitted",
          failed: "Failed to submit the report",
          alreadyApproved: "Report already approved by supervisor",
          backfillExceeded: "Too far back to edit. Contact your supervisor.",
        },
      },
      tabel: {
        warning: {
          over: "{{hours}} — above the daily norm ({{norm}}).",
          overApprovalNote: "Manager approval required.",
          under: "{{hours}} — below the daily norm ({{norm}}).",
        },
      },
    },
    changePassword: {
      title: "Change password",
      loggedInAs: "Signed in as {{name}} ({{email}})",
      logout: "Sign out",
      cancel: "Cancel",
      current: "Current password",
      next: "New password",
      confirm: "Confirmation",
      submit: "Save",
      submitting: "Saving…",
      required: "This field is required",
      mismatch: "Passwords do not match",
      invalidCurrent: "Current password is incorrect",
      networkError: "Network error, please try again",
      policy: {
        heading: "Password requirements",
        length: "At least 12 characters long",
        minClasses: "At least 3 of 4 character types",
        lower: "lowercase letters",
        upper: "uppercase letters",
        digit: "digits",
        special: "special characters",
        error:
          "Password does not meet the policy: at least 12 characters and at least 3 of 4 character types (lowercase, uppercase, digits, special).",
      },
    },
    gantt: {
      criticalMarker: "Critical path",
      cpToggle: {
        label: "Critical path",
        ariaOn: "Hide critical path",
        ariaOff: "Show critical path",
      },
      search: {
        empty: "No tasks match «{{query}}»",
        clear: "Clear filter",
      },
      toolbar: {
        zoom: {
          day: "Day",
          week: "Week",
          month: "Month",
          quarter: "Quarter",
        },
        today: {
          label: "Jump to today",
          lineToggle: "Today line",
        },
        density: {
          compact: "Compact",
          normal: "Normal",
        },
        cp: {
          label: "Crit path",
          counter: "{{cp}}/{{total}}",
          days: "{{days}} d",
          progress: "{{progress}}%",
        },
        search: {
          placeholder: "Find task…",
          ariaLabel: "Search tasks",
        },
        download: {
          label: "Download",
          notReady: "Export is not ready yet — coming in a later release",
        },
      },
      legend: {
        task: "Task",
        cp: "Critical path",
        milestone: "Milestone",
        dep: "Dependency",
        depCp: "CP dependency",
        today: "Today",
        projectColor: "Color = project",
      },
      help: {
        aria: "Gantt legend",
        title: "Legend",
      },
    },
  },
};
